import { Label } from '@higgins/ui';
import { toggleTag } from '@higgins/m2q-pricing';
import { ACTION_FOR_BAND, ACTIONS, areaRating, DELIVERY_MODES, deliveryModeForBand, outlookYears, ratingText, scaleOf } from '../lib/condition';
import { measuredSurfaces, ratingLines, type RatingLine } from '../lib/condition-lines';
import { allObservationsOf, ensureVisitArea, observationsOf, removeRating, restoreRating, saveObservation, updateVisitArea, visitAreaIdFor, visitAreaOf } from '../lib/condition-records';
import { shownPhotos, type PhotoSources } from '../lib/area-photos';
import { patchFields, type DeviceRecord, type Fields } from '../lib/device-db';
import { useDeviceQuery } from '../lib/hooks';
import { loadConditionTicks, loadSurfaceTypes } from '../lib/reference';
import { AddConditionSurface } from './add-condition-surface';
import { AreaPhotoMissing } from './area-photo';
import { PhotoStrip } from './photo-strip';
import { SavedTextField } from './saved-text-field';
import { SuggestedChoice } from './suggested-choice';
import { SurfaceRating } from './surface-rating';
import { canWriteTicks, conditionTicks, saveTicks, visitAreaTickOwner } from '../lib/tick-rows';

const SIDES = ['Internal', 'External'] as const;
const NOT_MEASURED = 'Not on the Measure tab';

/**
 * One area on the Condition tab. Nothing is recorded for the area in CRM
 * until something is entered; the first entry makes its visit area.
 */
/** `photoSources`: the job's photos, read once by the page. */
export function ConditionPanel({ visit, area, records, photoSources }: { visit: DeviceRecord; area: DeviceRecord; records: DeviceRecord[]; photoSources: PhotoSources }) {
  const reference = useDeviceQuery(async () => ({ tags: await loadConditionTicks(), types: (await loadSurfaceTypes()).list }), []);
  const visitArea = visitAreaOf(records, area.id);
  const observations = visitArea ? observationsOf(records, visitArea.id) : [];
  const scale = scaleOf(visit.fields.hig_ratingscale);
  const side = typeof area.fields.hig_side === 'string' ? area.fields.hig_side : null;
  const surfaces = measuredSurfaces(area.fields.hig_surfaces);
  // Before anything is recorded the visit area isn't made yet, but its id is known.
  // Removed lines (#509) are hidden, but "+ Add surface" still sees them, so it never adds a second surface of a removed type.
  const lines = ratingLines(surfaces, visitArea?.id ?? visitAreaIdFor(visit, area), visitArea ? allObservationsOf(records, visitArea.id) : []);
  const rows = lines.filter((l) => !l.removed);
  const rated = areaRating(observations.map((o) => ({ rating: typeof o.fields.hig_rating === 'string' ? o.fields.hig_rating : null })), scale.key);
  const action = typeof visitArea?.fields.hig_priority === 'string' ? visitArea.fields.hig_priority : null;
  const suggested = rated ? ACTION_FOR_BAND[rated.band] : null;
  const deliveryMode = typeof visitArea?.fields.hig_deliverymode === 'string' ? visitArea.fields.hig_deliverymode : null;
  const outlookYear = typeof visitArea?.fields.hig_outlookyear === 'number' ? visitArea.fields.hig_outlookyear : null;
  const tickDoc = conditionTicks(records, visitArea, reference?.tags ?? []);
  const offeredTicks = (reference?.tags ?? []).filter((t) => !t.side || t.side === 'Both' || !side || t.side === side);
  const offeredTypes = (reference?.types ?? []).filter((t) => !t.side || t.side === 'Both' || !side || t.side === side);
  const photos = shownPhotos(area, photoSources);

  async function visitAreaNow(): Promise<DeviceRecord> {
    return ensureVisitArea(visit, area);
  }

  /** The area's name or side changed: on the area, and on its visit area so CRM lists read right. */
  async function changeArea(changes: Fields) {
    await patchFields(area.id, changes);
    if (visitArea) await updateVisitArea(visitArea, changes);
  }

  async function changeVisitArea(changes: Fields) {
    await updateVisitArea(await visitAreaNow(), changes);
  }

  /** A condition tick on or off: a row per tick (lib/tick-rows.ts). */
  async function toggleTick(key: string) {
    const tags = reference?.tags ?? [];
    if (canWriteTicks(tags)) await saveTicks(visitAreaTickOwner(await visitAreaNow()), tickDoc, toggleTag(tickDoc, key), tags);
  }

  async function changeSurface(row: RatingLine, index: number, changes: Fields) {
    await saveObservation(visit, await visitAreaNow(), row, index, changes);
  }

  async function removeSurface(row: RatingLine, index: number) {
    await removeRating(visit, await visitAreaNow(), row, index);
  }

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-48 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Label htmlFor="condition-area-name">Area name</Label>
            {/* The feature photo is taken on the Measure tab; its flag shows here too (#437). */}
            <AreaPhotoMissing area={area} sources={photoSources} />
          </div>
          {/* The area's own name: one area, one name, typed here or on the Measure tab. */}
          <SavedTextField id="condition-area-name" label="Area name" value={String(area.fields.hig_name ?? '')} onSave={(name) => changeArea({ hig_name: name })} className="mt-1" />
        </div>
        <div>
          <Label>Side</Label>
          <div className="mt-1 flex gap-2">
            {SIDES.map((s) => (
              <button key={s} type="button" onClick={() => void changeArea({ hig_side: s })} className={`min-h-11 rounded-md border px-3 text-sm ${side === s ? 'border-primary bg-primary text-primary-foreground' : 'border-slate-300 bg-white text-slate-700'}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {offeredTicks.map((tag) => {
            const on = tickDoc.tags.includes(tag.key);
            return (
              <button key={tag.key} type="button" aria-pressed={on} onClick={() => void toggleTick(tag.key)} className={`min-h-11 rounded-full border px-3 text-sm ${on ? 'border-primary bg-orange-50 text-orange-800' : 'border-slate-300 text-slate-700'}`}>
                {on ? '✓ ' : ''}
                {tag.label}
              </button>
            );
          })}
          {offeredTicks.length === 0 && <p className="text-xs text-slate-500">No condition ticks for this side.</p>}
          {offeredTicks.length > 0 && !canWriteTicks(offeredTicks) && <p className="text-xs text-slate-500">The tick list on this device is out of date — open a job while you have signal to change these.</p>}
        </div>
        <p className="text-sm text-slate-700">
          Area rating <span className="ml-1 rounded-md bg-slate-100 px-2 py-1 font-medium">{rated ? ratingText(scale.key, rated.value) : 'not rated'}</span>
        </p>
      </div>

      <div>
        <p className="text-xs text-slate-500">Each surface: tap its rating. The area takes its worst surface.</p>
        {rows.map((row) => (
          <SurfaceRating
            key={row.id}
            name={row.name}
            baseSurface={row.baseSurface}
            hint={row.measured ? undefined : NOT_MEASURED}
            side={side}
            scale={scale}
            observation={row.observation}
            onChange={(changes) => void changeSurface(row, lines.indexOf(row), changes)}
            onRemove={() => void removeSurface(row, lines.indexOf(row))}
          />
        ))}
        {rows.length === 0 && <p className="py-2 text-sm text-slate-500">Nothing measured here yet. Add the surfaces to rate.</p>}
        {surfaces && <AddConditionSurface areaId={area.id} types={offeredTypes} lines={lines} onRestore={(line) => void restoreRating(visit, line)} />}
      </div>

      <SuggestedChoice label="Action" options={ACTIONS} chosen={action} suggested={suggested} onChoose={(a) => void changeVisitArea({ hig_priority: a })} />
      <SuggestedChoice label="Delivery mode" options={DELIVERY_MODES} chosen={deliveryMode} suggested={rated ? deliveryModeForBand(rated.band) : null} onChoose={(m) => void changeVisitArea({ hig_deliverymode: m })} />
      <SuggestedChoice
        label="Outlook year"
        options={outlookYears(new Date().getFullYear(), outlookYear).map(String)}
        chosen={outlookYear === null ? null : String(outlookYear)}
        suggested={null}
        onChoose={(y) => void changeVisitArea({ hig_outlookyear: y === null ? null : Number(y) })}
      />

      <div>
        <Label>Photos</Label>
        <p className="text-xs text-slate-500">The same photos as the Measure tab. Each records where it was taken, for the site plan.</p>
        <div className="mt-2">
          <PhotoStrip jobId={area.jobId} areaId={area.id} surface={null} photos={photos} />
        </div>
      </div>
    </div>
  );
}
